import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, CheckCircle, ArrowLeft, MessageCircle, Package, TrendingUp, User as UserIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import { AppLayout } from '../../../components/layout/AppLayout';
import { useFeatures } from '../../../contexts/FeatureContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const FindRunnerPage = () => {
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatures();
  const [runners, setRunners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    area: '',
  });
  
  const connectEnabled = isFeatureEnabled('connect_social');

  useEffect(() => {
    fetchRunners();
  }, []);

  const fetchRunners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/pickup/driver/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Filter only verified runners
      const verifiedRunners = (response.data.drivers || []).filter(
        driver => driver.verification_status === 'verified'
      );
      setRunners(verifiedRunners);
    } catch (error) {
      console.error('Error fetching runners:', error);
      toast.error('Failed to load runners');
    } finally {
      setLoading(false);
    }
  };

  // Filter runners based on search and filters
  const filteredRunners = runners.filter(runner => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!runner.name?.toLowerCase().includes(searchLower) &&
          !runner.city?.toLowerCase().includes(searchLower) &&
          !runner.area?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Location filters
    if (filters.country && !runner.country?.toLowerCase().includes(filters.country.toLowerCase())) {
      return false;
    }
    if (filters.city && !runner.city?.toLowerCase().includes(filters.city.toLowerCase())) {
      return false;
    }
    if (filters.area && !runner.area?.toLowerCase().includes(filters.area.toLowerCase())) {
      return false;
    }

    return true;
  });

  const handleRequestRunner = (runner) => {
    navigate('/pickup/post', { state: { preferredRunner: runner } });
  };

  const handleChatRunner = async (runner) => {
    try {
      // Create or open conversation with runner
      const response = await axios.post(
        `${API_URL}/api/messaging/conversations`,
        { participant_id: runner.user_id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      navigate(`/messages?conversation=${response.data.conversation_id}`);
    } catch (error) {
      console.error('Error opening chat:', error);
      toast.error('Failed to open chat');
    }
  };

  const RunnerCard = ({ runner }) => {
    return (
      <Card className="p-6 hover:shadow-xl transition-all border-2 hover:border-blue-500">
        {/* Header with Photo */}
        <div className="flex items-start gap-4 mb-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-2xl overflow-hidden">
              {runner.profile_picture ? (
                <img src={runner.profile_picture} alt={runner.name} className="w-full h-full object-cover" />
              ) : (
                runner.name?.[0] || 'R'
              )}
            </div>
            {runner.verification_status === 'verified' && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                <CheckCircle className="h-4 w-4 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1">
            <h3 className="text-xl font-bold mb-1">{runner.name || 'Runner'}</h3>
            
            {/* Rating */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold">
                  {runner.rating ? runner.rating.toFixed(1) : '5.0'}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                ({runner.completed_deliveries || 0} jobs)
              </span>
            </div>

            {/* Verification Badge */}
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle className="h-3 w-3 mr-1" />
              Verified Runner
            </Badge>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2 text-sm mb-4 text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>
            {runner.area && `${runner.area}, `}{runner.city}, {runner.country}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="text-center">
            <Package className="h-5 w-5 mx-auto text-blue-600 mb-1" />
            <p className="text-sm font-semibold">{runner.completed_deliveries || 0}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
          <div className="text-center">
            <Star className="h-5 w-5 mx-auto text-yellow-500 mb-1" />
            <p className="text-sm font-semibold">{runner.rating ? runner.rating.toFixed(1) : '5.0'}</p>
            <p className="text-xs text-muted-foreground">Rating</p>
          </div>
          <div className="text-center">
            <TrendingUp className="h-5 w-5 mx-auto text-green-600 mb-1" />
            <p className="text-sm font-semibold">{runner.success_rate || 100}%</p>
            <p className="text-xs text-muted-foreground">Success</p>
          </div>
        </div>

        {/* Services Offered */}
        {runner.services_offered && runner.services_offered.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-2">Services:</p>
            <div className="flex flex-wrap gap-1">
              {runner.services_offered.map((service, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {service}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleChatRunner(runner)}
            className="gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            Chat
          </Button>
          <Button
            size="sm"
            onClick={() => handleRequestRunner(runner)}
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            Request Runner
          </Button>
        </div>
        
        {/* Connect Profile Link */}
        {connectEnabled && runner.username && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/connect/profile/${runner.username}`)}
            className="w-full mt-2 gap-2 text-xs"
          >
            <UserIcon className="h-3 w-3" />
            View Connect Profile
          </Button>
        )}
      </Card>
    );
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b sticky top-0 z-10 px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pickup')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Find a Runner
              </h1>
              <p className="text-sm text-muted-foreground">
                {filteredRunners.length} verified {filteredRunners.length === 1 ? 'runner' : 'runners'} available
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Location Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
            <Input
              placeholder="Country"
              value={filters.country}
              onChange={(e) => setFilters({ ...filters, country: e.target.value })}
            />
            <Input
              placeholder="City"
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            />
            <Input
              placeholder="Area"
              value={filters.area}
              onChange={(e) => setFilters({ ...filters, area: e.target.value })}
            />
          </div>

          {Object.values(filters).some(v => v) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ country: '', city: '', area: '' })}
              className="mt-2"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Runners Grid */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
              <p className="mt-4 text-muted-foreground">Loading runners...</p>
            </div>
          ) : filteredRunners.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No runners found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || Object.values(filters).some(v => v)
                  ? 'Try adjusting your search or filters'
                  : 'No verified runners available yet'}
              </p>
              <Button onClick={() => navigate('/pickup/driver/register')}>
                Become a Runner
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRunners.map(runner => (
                <RunnerCard key={runner.user_id} runner={runner} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default FindRunnerPage;
